import type { CameraZoomSetting, CharacterHomeEffects, DisplaySettingId, DisplaySettings } from './types/display-settings';
import { clamp } from './utils';

const DISPLAY_SETTING_IDS: readonly DisplaySettingId[] = [
    'showPartyDamageNumbers',
    'showEnemyEssenceBars',
    'showPartyEssenceBars',
    'showOwnEssenceBar',
];

/** Display settings for an account that has never changed one: only enemy essence bars on. */
export function createDefaultDisplaySettings(): DisplaySettings {
    return {
        showPartyDamageNumbers: false,
        showEnemyEssenceBars: true,
        showPartyEssenceBars: false,
        showOwnEssenceBar: false,
    };
}

/**
 * Validates display settings (e.g. from a PATCH body or a stored setting).
 * A setting left out keeps its default, so settings added later don't
 * discard the ones an account already saved; any present must be a boolean,
 * or the result is undefined.
 */
export function parseDisplaySettings(value: unknown): DisplaySettings | undefined {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const candidate = value as Partial<Record<DisplaySettingId, unknown>>;
    const settings = createDefaultDisplaySettings();
    for (const id of DISPLAY_SETTING_IDS) {
        const enabled = candidate[id];
        if (enabled === undefined) {
            continue;
        }
        if (typeof enabled !== 'boolean') {
            return undefined;
        }
        settings[id] = enabled;
    }
    return settings;
}

/** Each Character Home effects level, from none to all, as the settings slider steps through them. */
export const CHARACTER_HOME_EFFECTS: readonly CharacterHomeEffects[] = ['off', 'minimal', 'all'];

/** Whether a value (e.g. from a PATCH body or a stored setting) is a Character Home effects level. */
export function isCharacterHomeEffects(value: unknown): value is CharacterHomeEffects {
    return CHARACTER_HOME_EFFECTS.includes(value as CharacterHomeEffects);
}

/**
 * How far the run's camera zooms: out to half the scale, in to twice it.
 * The HUD is page UI and overhead bars and labels are drawn in screen
 * pixels, so neither scales.
 */
export const MIN_CAMERA_ZOOM = 0.5;
export const MAX_CAMERA_ZOOM = 2;
/** One wheel notch (100 px of `deltaY` in Chrome) zooms by 10%; a trackpad's small deltas zoom smoothly. */
const ZOOM_PER_WHEEL_PIXEL = Math.log(1.1) / 100;

/** The camera zoom for an account that has never changed it: the game's own scale, unlocked. */
export function createDefaultCameraZoomSetting(): CameraZoomSetting {
    return { level: 1, isLocked: false };
}

/**
 * Validates a camera zoom setting (e.g. from a PATCH body or a stored
 * setting). A part left out keeps its default; `level` must be within the
 * zoom limits and `locked` a boolean, or the result is undefined.
 */
export function parseCameraZoomSetting(value: unknown): CameraZoomSetting | undefined {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const candidate = value as Partial<Record<keyof CameraZoomSetting, unknown>>;
    const defaults = createDefaultCameraZoomSetting();
    const level = candidate.level ?? defaults.level;
    const locked = candidate.isLocked ?? defaults.isLocked;
    if (typeof level !== 'number' || !Number.isFinite(level) || level < MIN_CAMERA_ZOOM || level > MAX_CAMERA_ZOOM) {
        return undefined;
    }
    if (typeof locked !== 'boolean') {
        return undefined;
    }
    return { level, isLocked: locked };
}

/** The zoom level after a wheel turn of `deltaY` pixels, as the browser reports it: toward the player zooms in, away zooms out. */
export function resolveWheelZoomLevel(level: number, deltaY: number): number {
    return clamp(level * Math.exp(-deltaY * ZOOM_PER_WHEEL_PIXEL), MIN_CAMERA_ZOOM, MAX_CAMERA_ZOOM);
}
